import {
    pipe, inc, range, map, not, pathOr,
} from 'ramda';
import { isNullOrEmpty } from './core';

export const CheckStatus = (response) => {
    if (response.status >= 200 && response.status < 300) {
        return response;
    }
    
    return response.text().then((message) => {
        let parsedMessage;
        let error;

        try {
            parsedMessage = JSON.parse(message);
        } catch {
            // Ignore parsing errors, parsedMessage will be null
        } finally {
            error = new Error(
                // Use a parsed message
                // Note: Response from a service call will contain an object
                // with a message property... so we should check for this
                not(isNullOrEmpty(parsedMessage)) ? pathOr(parsedMessage, ['message'], parsedMessage) :
                    // Fallback to a raw message
                    not(isNullOrEmpty(message)) ? message :
                        // If nothing else, print the status code and url
                        `${response.status} - ${decodeURI(response.url)}`,
            );
        }

        throw error;
    });
};

export const HandleConflict = (response) => {

    if (response.status === 409) {
        return response.json()
            .then((conflictingDependents) => {
                const error = { wasConflict: true, conflictingDependents };
                throw error;
            });
    }

    return response;
};

export const Parse = function (response) {
    if (response.status === 204) {
        return;
    }

    if (response.headers.has('Content-Type')) {
        const contentType = response.headers.get('Content-Type');

        if (contentType.includes('application/json')) {
            return response.json();
        }

        if (contentType.includes('text/html')) {
            return response.text();
        }

        console.warn('Unexpected content type encountered', contentType);
        return response.text();
    }

    console.warn('No Content-Type header was found on the response');
    return (response && response.json()) || null;
};

export const ParseText = function (response) {
    return response && response.text();
};

// For endoints that are paged

/**
 * @function fetchAllPaged
 * @description Fetch all items from a paged endoint in defined page sized chunks
 * @param source Source function that performs the ajax requests
 * @param params Parameters sent on each request
 * @param pageSize Page size of each individual request
 * @export
 * @async
 */
export const fetchAllPaged = async (source, params, pageSize) => {

    const { items, _meta: { total } } = await source({
        page: 1,
        pageSize,
        ...params,
    });

    const totalNumberOfPages = Math.ceil(total / pageSize);

    if (totalNumberOfPages > 1) {

        const allOtherPageResponses = await pipe(
            inc, // add one page to total to get correct range
            range(2), // get range of page numbers starting from second page
            map(pageNumber => source({
                page: pageNumber,
                pageSize,
                ...params,
            })),
            requests => Promise.all(requests),
        )(totalNumberOfPages);

        // concatinate and return all items from all responses
        return allOtherPageResponses.reduce(
            (accumulation, { items: pageOfItems }) => [...accumulation, ...pageOfItems],
            items,
        );
    }

    return items;
};

/**
 * @function fetchAndParse
 * @description Wraps the native fetch function with body stringifying and response checking and parsing 
 */
export const fetchAndParse = ({ url, method, headers, body = null }) => {

    const requestBody = method.toLowerCase() !== 'get' && body !== null 
        ? JSON.stringify(body) 
        : null;

    const request = { method, headers };

    if (requestBody !== null) {
        request.body = requestBody;
    }

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

/**
 * @function createValueElementIdReference
 * @description Returns a ValueElementIdReference with the ids, developerName and ContentType provided.
 * Each configuration value in a ServiceElementRequest needs a ValueElementIdReference
 * @param externalId externalId for the ValueElementIdReference (id in external systems)
 * @param internalId internalId for the ValueElementIdReference (id within Flow)
 * @param DeveloperName DeveloperName (name entered by value creator) for the value
 * @param ContentType Type of the Value (ContentString, ContentPassword etc.)
 * @param id id for the configuration value saved in the Tenant
 * @returns a populated ValueElementIdReference
 * @export
 */
const createValueElementIdReference = ({ developerName, contentType, id, propertyId }) => [
    {
        developerName: 'ValueElementIdReference',
        properties: [
            {
                contentValue: developerName,
                developerName: 'DeveloperName',
            },
            {
                contentValue: 'VARIABLE',
                developerName: 'ElementType',
            },
            {
                contentValue: contentType,
                developerName: 'ContentType',
            },
            {
                contentValue: id === 'null' ? null : id,
                developerName: 'Id',
            },
            {
                contentValue: propertyId === 'null' ? null : propertyId,
                developerName: 'TypeElementPropertyId',
            },
        ],
    },
];

/**
 * @function populateServiceValueRequest
 * @description Returns a ServiceValueRequest with each ValueElementIdReference populated with the given ids.
 * Converts draw/2/refreshService output data (which lists service configuration values available)
 * with a set of value ids for those configuration Values
 * into a form that draw/2/gettypesandactions accepts
 * @param serviceOutput draw/2/refreshService output data (which lists service configuration values available)
 * @param valueIdObject ids for Values that exist in the Tenant that are to be used for each configuration value in the service
 * @returns service configuration information, with valueElementIdReferences populated for use with draw/2/refreshService
 * @export
 */
export const populateServiceValueRequest = (serviceOutput, valueIdObject) => serviceOutput.find(
    output => output.developerName === 'Configuration Values',
).objectData.map(
    (serviceValueRequest) => {
        const name = serviceValueRequest.properties.find(property => property.developerName === 'DeveloperName').contentValue;
        const meta = valueIdObject.find(value => value.name === name).value;

        // If there is an id, then create a ValueElementIdReference for the Value
        if (manywho.utils.isNullOrEmpty(meta) === false) {
            serviceValueRequest.properties.find(
                property => property.developerName === 'ValueElementToReferenceId',
            ).objectData = createValueElementIdReference(meta);
        }
        return serviceValueRequest;
    },
);
